import { type SelectOption } from '@ibe/ui';

/**
 * The time-zone vocabulary every form in the portal picks from.
 *
 * Shared rather than declared beside each form: a property and a tenant answer
 * the same question, and two copies of the list drift the moment one of them
 * gains a special case.
 */

/**
 * Whether a string names a time zone this runtime knows.
 *
 * The same check the services make, run here so "Australia/Melborne" is a
 * message beside the field instead of a 422.
 */
export function isKnownTimezone(value: string): boolean {
  try {
    new Intl.DateTimeFormat('en-US', { timeZone: value });
    return true;
  } catch {
    return false;
  }
}

/**
 * Every IANA zone this runtime knows, plus UTC.
 *
 * `supportedValuesOf` deliberately omits UTC — it lists canonical zone names,
 * and UTC is an alias for Etc/UTC — but it is the platform's default and a
 * legitimate answer, so it is put back at the top.
 *
 * A list rather than a text input because "Australia/Melborne" is a 422 the
 * operator cannot see coming, and because the list is searchable.
 */
export const TIMEZONE_OPTIONS: readonly SelectOption[] = [
  ...Intl.supportedValuesOf('timeZone'),
].map((zone) => ({ value: zone, label: zone }));
